import { z } from 'zod';
import { datePl, dayDiff, shiftDate } from './planning';
import type { FieldProtocol } from './protocol';

/** Instalacje, urządzenia, serwis i przeglądy (P6, docs/02 rozdz. 9–10). */

export const INSTALLATION_STATUSES = { planowana: 'Planowana', w_budowie: 'W budowie', czynna: 'Czynna', wylaczona: 'Wyłączona' } as const;
export type InstallationStatus = keyof typeof INSTALLATION_STATUSES;

export const DEVICE_STATUSES = { czynne: 'Czynne', wymienione: 'Wymienione', zdemontowane: 'Zdemontowane' } as const;
export type DeviceStatus = keyof typeof DEVICE_STATUSES;

export const TICKET_TYPES = { przeglad: 'Przegląd', awaria: 'Awaria', reklamacja: 'Reklamacja', gwarancja: 'Naprawa gwarancyjna' } as const;
export type TicketType = keyof typeof TICKET_TYPES;

export const TICKET_STATUSES = {
  nowe: 'Nowe',
  do_umowienia: 'Do umówienia',
  zaplanowane: 'Zaplanowane',
  w_realizacji: 'W realizacji',
  wykonane: 'Wykonane',
  zamkniete: 'Zamknięte',
  anulowane: 'Anulowane',
} as const;
export type TicketStatus = keyof typeof TICKET_STATUSES;
/** zgłoszenia otwarte — na plan przypada najwyżej jedno otwarte zgłoszenie przeglądu */
export const OPEN_TICKET_STATUSES: TicketStatus[] = ['nowe', 'do_umowienia', 'zaplanowane', 'w_realizacji'];

export const TICKET_PRIORITIES = { niski: 'Niski', normalny: 'Normalny', wysoki: 'Wysoki', pilny: 'Pilny' } as const;
export const CLAIM_STATES = { brak: 'Nie zgłoszono', zgloszone: 'Zgłoszone u producenta', zaakceptowane: 'Zaakceptowane', odrzucone: 'Odrzucone', rozliczone: 'Rozliczone' } as const;
/** QA6 P29: reklamacja otwarta u producenta (do pilnowania także po zamknięciu zgłoszenia) */
export const CLAIM_OPEN_STATES = ['zgloszone', 'zaakceptowane'] as const;
/** QA qa10302243 P5: domyślny termin decyzji producenta od zgłoszenia reklamacji (przypomnienie dla odpowiedzialnej osoby) */
export const CLAIM_REMINDER_DAYS = 14;
/** QA 10310111 3.8: decyzja producenta — wymaga numeru reklamacji („zgłoszone” — numer może przyjść później, tylko ostrzeżenie) */
export const CLAIM_DECISION_STATES = ['zaakceptowane', 'odrzucone', 'rozliczone'] as const;
export const CLAIM_NUMBER_REQUIRED = 'Podaj numer reklamacji u producenta — wymagany przy decyzji producenta';
export const claimNumberMissing = (state: string | null | undefined, number: string | null | undefined): boolean =>
  (CLAIM_DECISION_STATES as readonly string[]).includes(state ?? '') && !(number ?? '').trim();

/**
 * QA qa10311123 3.3: rozliczenie naprawy (minimum do decyzji F-12, docs/09). „Gwarancja” = znacznik `warranty` zgłoszenia;
 * „płatna” i „bez opłaty” — wpis historii (bez nowej kolumny). Płatna po zamknięciu — powiadomienie księgowości
 * „Wystaw fakturę za serwis” do wpisania numeru faktury. Ceny i pozycje faktury — poza systemem do decyzji F-12.
 */
export const TICKET_BILLING = { gwarancja: 'Gwarancja', platna: 'Płatna — faktura dla klienta', bez_oplaty: 'Bez opłaty' } as const;
export type TicketBilling = keyof typeof TICKET_BILLING;
export const TICKET_BILLING_KEYS = Object.keys(TICKET_BILLING) as [TicketBilling, ...TicketBilling[]];
/** Powód wymagany: „bez opłaty” albo zmiana zgłoszenia gwarancyjnego na płatne (zapisany w historii). */
export const billingReasonRequired = (billing: TicketBilling | null | undefined, warranty: boolean): boolean => billing === 'bez_oplaty' || (billing === 'platna' && warranty);
export const BILLING_REASON_REQUIRED = 'Podaj powód rozliczenia (zapisze się w historii zgłoszenia)';
export const TICKET_BILLING_STATES = { do_faktury: 'Do zafakturowania — czeka na księgowość', zafakturowane: 'Zafakturowane' } as const;
export type TicketBillingState = keyof typeof TICKET_BILLING_STATES;

// ── plan przeglądów ───────────────────────────────────────────────────────

export const PLAN_ANCHORS = {
  wykonanie: 'Od daty wykonania poprzedniego',
  planowy: 'Od daty planowej poprzedniego (stały rytm)',
} as const;
export type PlanAnchor = keyof typeof PLAN_ANCHORS;
export type CycleUnit = 'miesiace' | 'dni';

/** Data + n miesięcy; dzień ponad koniec miesiąca → ostatni dzień (31.01 + 1 mies. = 28/29.02). */
export function addMonths(date: string, months: number): string {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7)) - 1 + months;
  const d = Number(date.slice(8, 10));
  const year = y + Math.floor(m / 12);
  const month = ((m % 12) + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

export interface PlanCycle {
  cycleUnit: CycleUnit;
  cycleValue: number;
}
export const addCycle = (date: string, c: PlanCycle, times = 1) => (c.cycleUnit === 'dni' ? shiftDate(date, c.cycleValue * times) : addMonths(date, c.cycleValue * times));

export interface PlanState extends PlanCycle {
  anchor: PlanAnchor;
  /** data, od której liczony jest bieżący termin (wykonanie poprzedniego / poprzedni termin planowy / uruchomienie) */
  baseDate: string;
  /** jednorazowe przesunięcie terminu (bez zmiany rytmu) */
  dueOverride: string | null;
}

/** Termin wynikający z rytmu (bez jednorazowego przesunięcia). */
export const rhythmDue = (p: PlanState) => addCycle(p.baseDate, p);
/** Obowiązujący termin przeglądu. */
export const planDue = (p: PlanState) => p.dueOverride ?? rhythmDue(p);

/**
 * Po wykonaniu przeglądu (docs/02 rozdz. 10 p. 3): tryb „wykonanie” — termin = data wykonania + cykl;
 * tryb „planowy” — poprzedni termin planowy + cykl, a gdy wypada nie później niż wykonanie — kolejny.
 */
export function completePlan(p: PlanState, doneDate: string): PlanState {
  if (p.anchor === 'wykonanie') return { ...p, baseDate: doneDate, dueOverride: null };
  let base = rhythmDue(p);
  for (let i = 0; i < 1000 && addCycle(base, p) <= doneDate; i++) base = addCycle(base, p);
  return { ...p, baseDate: base, dueOverride: null };
}

/** Zmiana cyklu przelicza tylko przyszły termin (p. 5) — baza zostaje, jednorazowe przesunięcie znika. */
export const changeCycle = (p: PlanState, c: PlanCycle): PlanState => ({ ...p, ...c, dueOverride: null });

/** Przesunięcie terminu: pojedyncze (rytm bez zmian) albo całego harmonogramu (nowa baza). */
export function shiftPlan(p: PlanState, newDue: string, whole: boolean): PlanState {
  if (!whole) return { ...p, dueOverride: newDue === rhythmDue(p) ? null : newDue };
  const base = p.cycleUnit === 'dni' ? shiftDate(newDue, -p.cycleValue) : addMonths(newDue, -p.cycleValue);
  return { ...p, baseDate: base, dueOverride: null };
}

export type PlanHealth = 'w_terminie' | 'do_umowienia' | 'po_terminie' | 'zalegly' | 'wstrzymany';
export const PLAN_HEALTH: Record<PlanHealth, string> = {
  w_terminie: 'W terminie',
  do_umowienia: 'Do umówienia',
  po_terminie: 'Po terminie (w tolerancji)',
  zalegly: 'Zaległy',
  wstrzymany: 'Wstrzymany',
};

/** Stan planu na dziś: zaległy = termin + tolerancja minęły (termin nie przesuwa się sam — p. 4). */
export function planHealth(due: string, today: string, o: { toleranceDays: number; leadDays: number; active: boolean }): PlanHealth {
  if (!o.active) return 'wstrzymany';
  const d = dayDiff(today, due);
  if (d < -o.toleranceDays) return 'zalegly';
  if (d < 0) return 'po_terminie';
  if (d <= o.leadDays) return 'do_umowienia';
  return 'w_terminie';
}

/** Dzień utworzenia zgłoszenia przeglądu (wyprzedzenie). */
export const ticketCreationDay = (due: string, leadDays: number) => shiftDate(due, -leadDays);

/** Gwarancja urządzenia trwa w danym dniu (data końca włącznie). */
export const warrantyValid = (until: string | null | undefined, today: string) => !!until && until >= today;

/** Krótki opis gwarancji przy urządzeniu: „gw. do 12.03.2030” / „po gwarancji (do 12.03.2024)”; bez daty — pusty. */
export function warrantyText(until: string | null | undefined, today: string): string {
  if (!until) return '';
  return warrantyValid(until, today) ? `gw. do ${datePl(until)}` : `po gwarancji (do ${datePl(until)})`;
}

// ── DTO ───────────────────────────────────────────────────────────────────

export interface DeviceDto {
  id: string;
  installationId: string;
  name: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  installedOn: string | null;
  commissionedOn: string | null;
  warrantyUntil: string | null;
  warrantyRequiresService: boolean;
  status: DeviceStatus;
  replacedById: string | null;
  replacesId: string | null;
  replacedOn: string | null;
  replaceReason: string;
  notes: string;
}

export interface ServicePlanDto extends PlanState {
  id: string;
  deviceId: string | null;
  installationId: string;
  toleranceDays: number;
  leadDays: number;
  active: boolean;
  pauseReason: string;
  lastDoneOn: string | null;
  due: string;
  health: PlanHealth;
  /** czynne urządzenie z trwającą gwarancją wymagającą przeglądów — zaległość planu = ryzyko utraty gwarancji */
  warrantyRequiresInspections: boolean;
  /** QA6 P28: przeglądy wykonane po terminie i tolerancji (od najnowszego) */
  lateInspections: LateInspectionDto[];
  openTicketId: string | null;
  openTicketNumber: string | null;
}

/** QA6 P28: przegląd wykonany po tolerancji — ślad przy urządzeniu na gwarancji. */
export interface LateInspectionDto {
  due: string;
  doneOn: string;
  daysLate: number;
  toleranceDays: number;
}

export interface InstallationDto {
  id: string;
  siteId: string;
  siteAddress: string;
  clientName: string | null;
  tradeCode: string;
  tradeName: string;
  companyId: string | null;
  companyCode: string | null;
  name: string;
  status: InstallationStatus;
  commissionedOn: string | null;
  sourceCaseId: string | null;
  sourceCaseNumber: string | null;
  devices: DeviceDto[];
  plans: ServicePlanDto[];
}

export interface DeviceHistoryEntry {
  at: string;
  kind: 'montaz' | 'uruchomienie' | 'protokol' | 'zgloszenie' | 'wymiana' | 'zmiana' | 'przeglad_po_terminie';
  label: string;
  link: { type: 'protokol' | 'zgloszenie' | 'urzadzenie' | 'sprawa'; id: string } | null;
}

export interface DeviceCardDto {
  device: DeviceDto;
  installation: Omit<InstallationDto, 'devices' | 'plans'>;
  replaces: { id: string; name: string; serialNumber: string } | null;
  replacedBy: { id: string; name: string; serialNumber: string } | null;
  plans: ServicePlanDto[];
  history: DeviceHistoryEntry[];
  protocols: (FieldProtocol & { taskNumber: string })[];
  can: { edit: boolean; plans: boolean; ticket: boolean };
}

export interface ServiceTicketDto {
  id: string;
  number: string;
  type: TicketType;
  status: TicketStatus;
  priority: keyof typeof TICKET_PRIORITIES;
  siteId: string;
  siteAddress: string;
  clientId: string | null;
  clientName: string | null;
  installationId: string | null;
  devices: { id: string; name: string; serialNumber: string }[];
  companyId: string | null;
  companyCode: string | null;
  planId: string | null;
  dueDate: string | null;
  reporter: string;
  description: string;
  warranty: boolean;
  claimNumber: string;
  claimState: keyof typeof CLAIM_STATES;
  /** wymieniono część: materiały wpisane w wizycie albo wymiana urządzenia instalacji po zgłoszeniu (U4 — reklamacja u producenta) */
  partsReplaced: boolean;
  /** części i materiały wpisane w wizytach zgłoszenia (QA5 P37); rozliczenie wg znacznika całego zgłoszenia */
  parts?: { taskNumber: string; name: string; quantity: string; unit: string }[];
  /** QA qa10301331 P2: pole „Wymienione części” podpisanych protokołów wizyt (liczy się jako wymiana części) */
  protocolParts?: { taskNumber: string; protocolCode: string; text: string }[];
  closeReason: string;
  createdAt: string;
  closedAt: string | null;
  overdue: boolean;
  tasks: { id: string; number: string; status: string; dateFrom: string | null; taskType: string }[];
  protocols: FieldProtocol[];
  /** QA7 P12: przeglądy instalacji wykonane po tolerancji w ciągu 12 miesięcy przed zgłoszeniem (i później) — od najnowszego */
  lateInspections: LateInspectionDto[];
  /** QA qa10301331 P19: zaległe przeglądy tej instalacji (otwarte zgłoszenie) — „umów razem z tą wizytą” */
  overduePlans?: { planId: string; deviceName: string | null; due: string; ticketId: string | null; ticketNumber: string | null }[];
  /** QA qa10302046 P20: pierwszy adres e-mail klienta (do dosłania protokołów) */
  clientEmail?: string | null;
  /** QA qa10302046 P20: podpisane protokoły z PDF, których nie wysłano klientowi (bez adresu przy podpisie, bez późniejszej wysyłki) */
  undeliveredProtocolIds?: string[];
  /** QA qa10302243 P5: reklamacja „zgłoszona” — termin decyzji producenta (przypomnienie), osoba odpowiedzialna, po terminie */
  claimDueOn?: string | null;
  claimOwnerName?: string | null;
  claimOverdue?: boolean;
  /** QA qa10302243 P6: protokoły doręczone poza e-mailem (na miejscu / wydruk) */
  handedOverProtocolIds?: string[];
  /** QA qa10311123 3.3: rozliczenie naprawy (null — nie wybrano) i stan faktury za serwis */
  billing?: TicketBilling | null;
  billingReason?: string;
  billingState?: TicketBillingState | null;
  billingInvoice?: { number: string; at: string; by: string | null } | null;
}

/** QA7 P12: opis spóźnionego przeglądu (zgłoszenie, telefon serwisanta). */
export function lateInspectionText(l: LateInspectionDto): string {
  const pl = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;
  return `Przegląd wykonany ${pl(l.doneOn)} — ${l.daysLate} dni po terminie ${pl(l.due)} (tolerancja ${l.toleranceDays} dni)`;
}

// ── walidacja wejścia ─────────────────────────────────────────────────────

const uuid = z.string().uuid();
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Nieprawidłowa data');

export const deviceUpdateSchema = z.object({
  name: z.string().trim().min(1).max(200),
  manufacturer: z.string().trim().max(100).default(''),
  model: z.string().trim().max(100).default(''),
  serialNumber: z.string().trim().max(80).default(''),
  installedOn: isoDay.nullable().default(null),
  commissionedOn: isoDay.nullable().default(null),
  warrantyUntil: isoDay.nullable().default(null),
  warrantyRequiresService: z.boolean().default(false),
  notes: z.string().max(2000).default(''),
  reason: z.string().trim().min(3, 'Podaj powód zmiany').max(500),
});

export const deviceCreateSchema = deviceUpdateSchema.omit({ reason: true }).extend({ installationId: uuid });

export const deviceReplaceSchema = z.object({
  date: isoDay,
  reason: z.string().trim().min(3, 'Podaj powód wymiany').max(500),
  ticketId: uuid.nullable().default(null),
  name: z.string().trim().min(1).max(200),
  manufacturer: z.string().trim().max(100).default(''),
  model: z.string().trim().max(100).default(''),
  serialNumber: z.string().trim().max(80).default(''),
  movePlans: z.boolean().default(true),
});

export const installationCreateSchema = z.object({
  siteId: uuid,
  tradeCode: z.string().min(2).max(4),
  name: z.string().trim().min(1).max(200),
  companyId: uuid.nullable().default(null),
});

export const planCreateSchema = z.object({
  installationId: uuid,
  deviceId: uuid.nullable().default(null),
  anchor: z.enum(['wykonanie', 'planowy']).default('wykonanie'),
  cycleUnit: z.enum(['miesiace', 'dni']).default('miesiace'),
  cycleValue: z.number().int().min(1).max(120).default(12),
  baseDate: isoDay,
  toleranceDays: z.number().int().min(0).max(180).default(30),
  leadDays: z.number().int().min(0).max(180).default(45),
});

export const planUpdateSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('cykl'), anchor: z.enum(['wykonanie', 'planowy']), cycleUnit: z.enum(['miesiace', 'dni']), cycleValue: z.number().int().min(1).max(120), toleranceDays: z.number().int().min(0).max(180), leadDays: z.number().int().min(0).max(180), reason: z.string().trim().min(3).max(500) }),
  z.object({ action: z.literal('przesun'), due: isoDay, whole: z.boolean(), reason: z.string().trim().min(3).max(500) }),
  z.object({ action: z.literal('wstrzymaj'), reason: z.string().trim().min(3).max(500) }),
  z.object({ action: z.literal('wznow'), reason: z.string().trim().min(3).max(500) }),
]);

export const ticketCreateSchema = z.object({
  type: z.enum(['przeglad', 'awaria', 'reklamacja', 'gwarancja']),
  siteId: uuid,
  installationId: uuid.nullable().default(null),
  deviceIds: z.array(uuid).max(50).default([]),
  clientId: uuid.nullable().default(null),
  companyId: uuid.nullable().default(null),
  priority: z.enum(['niski', 'normalny', 'wysoki', 'pilny']).default('normalny'),
  reporter: z.string().trim().max(200).default(''),
  description: z.string().trim().min(3, 'Opisz zgłoszenie').max(4000),
  warranty: z.boolean().default(false),
  /** rozliczenie naprawy wybrane od razu przy dodaniu (pominięte — wg `warranty`); „gwarancja” ustawia `warranty` */
  billing: z.enum(TICKET_BILLING_KEYS).optional(),
  billingReason: z.string().trim().max(500).optional(),
  dueDate: isoDay.nullable().default(null),
  /** QA qa10302243 P6: adres e-mail dopisywany klientowi bez adresu (do wysyłki protokołu) */
  clientEmail: z.string().trim().toLowerCase().email('Nieprawidłowy adres e-mail').max(200).nullable().optional(),
});

export const ticketUpdateSchema = z.object({
  priority: z.enum(['niski', 'normalny', 'wysoki', 'pilny']),
  description: z.string().trim().min(3).max(4000),
  warranty: z.boolean(),
  companyId: uuid.nullable(),
  claimNumber: z.string().trim().max(80).default(''),
  claimState: z.enum(['brak', 'zgloszone', 'zaakceptowane', 'odrzucone', 'rozliczone']).default('brak'),
  /** QA qa10302243 P5: termin decyzji producenta (pominięty — domyślnie CLAIM_REMINDER_DAYS dni od zgłoszenia) */
  claimDueOn: isoDay.nullable().optional(),
  /** urządzenia zgłoszenia (D15) — pominięte: bez zmian */
  deviceIds: z.array(uuid).max(50).optional(),
  /** QA qa10311123 3.3: rozliczenie naprawy (pominięte — wg `warranty`); „gwarancja” ustawia `warranty` */
  billing: z.enum(TICKET_BILLING_KEYS).optional(),
  billingReason: z.string().trim().max(500).default(''),
}).refine((v) => !claimNumberMissing(v.claimState, v.claimNumber), { message: CLAIM_NUMBER_REQUIRED, path: ['claimNumber'] });

export const ticketStatusSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('do_umowienia') }),
  z.object({ action: z.literal('wykonane'), date: isoDay, note: z.string().trim().min(3).max(500) }),
  z.object({
    action: z.literal('zamknij'),
    note: z.string().trim().max(500).default(''),
    // QA6 P29: reklamacja u producenta wpisywana przy zamknięciu (pominięte — bez zmian)
    claimNumber: z.string().trim().max(80).optional(),
    claimState: z.enum(['brak', 'zgloszone', 'zaakceptowane', 'odrzucone', 'rozliczone']).optional(),
    claimDueOn: isoDay.nullable().optional(),
    // QA qa10311123 3.3: rozliczenie naprawy wybrane przy zamknięciu (pominięte — bez zmian)
    billing: z.enum(TICKET_BILLING_KEYS).optional(),
    billingReason: z.string().trim().max(500).default(''),
  }),
  z.object({ action: z.literal('anuluj'), note: z.string().trim().min(3, 'Podaj powód').max(500) }),
]);

/** QA qa10311123 3.3: zmiana rozliczenia naprawy (także po zamknięciu zgłoszenia). */
export const ticketBillingSchema = z.object({ billing: z.enum(TICKET_BILLING_KEYS), reason: z.string().trim().max(500).default('') });

/** QA qa10311123 3.3: faktura za serwis wystawiona (numer z modułu faktur albo Fakturowni). */
export const ticketInvoicedSchema = z.object({ invoiceNumber: z.string().trim().min(1, 'Podaj numer faktury').max(80) });

export const ticketTaskSchema = z.object({ taskType: z.enum(['przeglad', 'serwis']), title: z.string().trim().max(200).default('') });

export const protocolVoidSchema = z.object({ reason: z.string().trim().min(5, 'Podaj powód unieważnienia').max(500) });
export const protocolResendSchema = z.object({ email: z.string().trim().toLowerCase().email('Nieprawidłowy adres e-mail'), reason: z.string().trim().min(3, 'Podaj powód').max(500) });

/** Podpis zdalny protokołu: wysyłka (lub ponowna wysyłka na inny adres) do podpisu elektronicznego. */
export const protocolEsignSendSchema = z
  .object({
    email: z.string().trim().toLowerCase().email('Nieprawidłowy adres e-mail'),
    channel: z.enum(['email', 'sms']).default('email'),
    phone: z.string().trim().max(20).default(''),
    signerName: z.string().trim().min(3, 'Podaj imię i nazwisko klienta').max(200),
    message: z.string().trim().max(1000).default(''),
  })
  .refine((v) => v.channel !== 'sms' || /^\+?[\d\s-]{9,}$/.test(v.phone), { message: 'Podaj numer telefonu do kodu SMS', path: ['phone'] });

/** Podpis zdalny protokołu: przejście na podpis papierowy (anuluje trwający e-podpis). */
export const protocolToPaperSchema = z.object({ reason: z.string().trim().min(3, 'Podaj powód').max(500) });

/** Podpis papierowy protokołu odnotowany w biurze (skan w polu „scan” formularza). */
export const protocolPaperSignSchema = z.object({
  signedOn: isoDay,
  signerName: z.string().trim().min(3, 'Podaj, kto podpisał').max(200),
  remarks: z.string().trim().max(2000).default(''),
});

/** QA qa10302243 P6: niedoręczone protokoły zgłoszenia doręczone poza e-mailem (na miejscu, wydruk) */
export const protocolHandedOverSchema = z.object({ note: z.string().trim().max(300).default('') });

/** QA6 P29: reklamacja u producenta — numer i stan, także po zamknięciu zgłoszenia. */
export const ticketClaimSchema = z.object({
  claimNumber: z.string().trim().max(80).default(''),
  claimState: z.enum(['brak', 'zgloszone', 'zaakceptowane', 'odrzucone', 'rozliczone']),
  claimDueOn: isoDay.nullable().optional(),
}).refine((v) => !claimNumberMissing(v.claimState, v.claimNumber), { message: CLAIM_NUMBER_REQUIRED, path: ['claimNumber'] });
